'use client';

// ChatMessage - Creator-specific wrapper around @boty/ui ChatBubble
// Keeps relative timestamp formatting logic with creator translations

import { ChatBubble } from '@boty/ui';
import { useTranslations } from 'next-intl';
import type { ChatMessage as ChatMessageType } from './types';

export interface ChatMessageProps {
  /** Message data */
  message: ChatMessageType;
  /** Whether to show timestamp */
  showTimestamp?: boolean;
}

/**
 * Calculate relative time components for i18n formatting
 */
function getRelativeTimeInfo(isoString: string): {
  type: 'justNow' | 'minutesAgo' | 'time';
  minutes?: number;
  timeString?: string;
} {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    // "Just now" for less than 1 minute
    if (diffMinutes < 1) {
      return { type: 'justNow' };
    }
    // "X min ago" for less than 60 minutes
    if (diffMinutes < 60) {
      return { type: 'minutesAgo', minutes: diffMinutes };
    }
    // Otherwise show time (e.g., "10:23 AM")
    return {
      type: 'time',
      timeString: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    };
  } catch {
    return { type: 'time', timeString: '' };
  }
}

/**
 * ChatMessage - Display a single chat message with WhatsApp-style bubbles
 *
 * Uses @boty/ui ChatBubble for consistent styling across apps.
 *
 * Features:
 * - User messages: indigo background, white text, right-aligned (swaps in RTL)
 * - Bot messages: light gray background, dark text, ALWAYS left-aligned (WhatsApp pattern)
 * - Relative timestamps ("Just now", "2 min ago") using creator translations
 * - Streaming text indicator
 */
export function ChatMessage({ message, showTimestamp = true }: ChatMessageProps) {
  const t = useTranslations('creator.testing');
  const isUser = message.role === 'user';

  // Format timestamp using i18n
  const formatTimestamp = (isoString: string): string => {
    const info = getRelativeTimeInfo(isoString);
    switch (info.type) {
      case 'justNow':
        return t('chat.timestamp.justNow');
      case 'minutesAgo':
        return t('chat.timestamp.minutesAgo', { count: info.minutes ?? 1 });
      case 'time':
      default:
        return info.timeString || '';
    }
  };

  const timestamp = showTimestamp && message.createdAt ? formatTimestamp(message.createdAt) : undefined;
  const isStreaming = message.isStreaming && !isUser;

  return (
    <ChatBubble
      sender={isUser ? 'user' : 'bot'}
      {...(timestamp ? { timestamp } : {})}
      {...(isStreaming ? { isStreaming: true } : {})}
      {...(!isUser ? { markdown: true } : {})}
      sx={{ mb: 2 }}
    >
      {message.content}
    </ChatBubble>
  );
}

export default ChatMessage;
